import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { WHATSAPP_URL } from "@majourneys/shared";
import { PW, pewterPanel, pewterPrimary } from "../internal-ui/pewter-shell";
import { PewterSelect } from "../internal-ui/pewter-pickers";
import { BookingSkinScope } from "../skin";
import { TransferPriceSummary } from "./transfer-price-summary";
import {
  PickerLabelled,
  TransferAlerts,
  TransferDetailsFields,
  TransferPeopleField,
  TransferPlaceBlock,
  TransferWhatsAppOnly,
  TransferWhenFields,
  useTransferBooking,
  type TransferFormProps,
} from "./transfer-booking";

export type { TransferFormProps, TransferRouteOption } from "./transfer-booking";

/**
 * The transfer booking form in clear numbered steps: route, places, date and
 * times, party size, the guest's details and a live total. Used in a sidebar
 * (activity pages, city sites). The state and rules live in
 * `useTransferBooking`, shared with the web app's ride card.
 */
export function TransferForm(props: TransferFormProps) {
  const { t } = useTranslation();
  const b = useTransferBooking(props);
  if (!b) return null;
  if (!b.PlaceField) return <TransferWhatsAppOnly />;
  const { uid, route, routes, pkg } = b;

  return (
    <BookingSkinScope skin="pewter">
      <div ref={b.rootRef} style={{ ...pewterPanel(22), color: PW.navy }} className="p-4 font-sans text-[14px] sm:p-5" data-testid="transfer-form">
        {routes.length > 1 && (
          <PickerLabelled id={`${uid}-route-label`} text={t("transport.route")} className="mb-4">
            <PewterSelect
              labelId={`${uid}-route-label`}
              title={t("transport.route")}
              value={route.slug}
              options={routes.map((r) => ({ value: r.slug, label: r.title }))}
              onChange={b.changeRoute}
            />
          </PickerLabelled>
        )}

        <FormStep n={1} id={`${uid}-step-where`} title={t("transport.step_where", { defaultValue: "Where" })}>
          <TransferPlaceBlock b={b} which="pickup" />
          <TransferPlaceBlock b={b} which="place" />
        </FormStep>

        <FormStep n={2} id={`${uid}-step-when`} title={t("transport.step_when", { defaultValue: "When" })}>
          <TransferWhenFields b={b} />
        </FormStep>

        <FormStep n={3} id={`${uid}-step-who`} title={t("transport.step_who", { defaultValue: "Who" })}>
          <TransferPeopleField b={b} />
        </FormStep>

        <FormStep n={4} id={`${uid}-step-details`} title={t("transport.step_details", { defaultValue: "Your details" })}>
          <TransferDetailsFields b={b} />
        </FormStep>

        <FormStep n={5} id={`${uid}-step-summary`} title={t("transport.step_summary", { defaultValue: "Your price" })}>
          {pkg && <TransferPriceSummary maxGuests={pkg.maxGuests ?? b.people} price={pkg.price} fee={b.fee} typedFeeNote={b.typedFeeNote} lang={b.lang} />}
          <TransferAlerts b={b} />
          <button type="button" disabled={b.submitting} aria-busy={b.submitting} onClick={() => void b.submit()} className="mt-3 h-12 w-full rounded-[14px] text-[15px] font-semibold disabled:opacity-60" style={pewterPrimary}>
            {b.sent ? t("booking_sheet.request_sent") : t("transport.book")}
          </button>
          <div className="mt-2 text-center text-[13px]" style={{ color: PW.muted }}>
            <a href={WHATSAPP_URL} className="inline-flex min-h-6 items-center gap-1.5 font-semibold underline" style={{ color: PW.navy }}>
              <Icon name="ri-whatsapp-line" className="text-[15px]" style={{ color: PW.green }} aria-hidden="true" />
              {t("transport.whatsapp")}
            </a>
          </div>
        </FormStep>
      </div>
    </BookingSkinScope>
  );
}

/**
 * One numbered step of the form: a small navy disc with the number, the
 * step's name as a heading, then its fields. A hairline separates steps.
 */
function FormStep({ n, id, title, children }: { n: number; id: string; title: string; children: ReactNode }) {
  return (
    <section
      aria-labelledby={id}
      className="border-t py-4 first:border-t-0 first:pt-0 last:pb-0"
      style={{ borderColor: PW.hairline }}
    >
      <h3 id={id} className="mb-3 flex items-center gap-2.5 text-start font-heading text-[15px] font-bold" style={{ color: PW.navy }}>
        <span
          aria-hidden="true"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-sans text-[12px] font-bold text-white"
          style={{ background: PW.navyGradient }}
        >
          {n}
        </span>
        {title}
      </h3>
      {children}
    </section>
  );
}
